import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  Archive, Building2, CarFront, CirclePlus, House, KeyRound, LogOut, Menu, Moon,
  PoundSterling, Search, ShieldCheck, SlidersHorizontal, Sun, Truck, Users, X, type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { CompanyPicker } from './CompanyPicker';
import { ROLE_LABELS, canCreateJobs, canInvoiceJobs, canManageCustomers, canPriceJobs } from '../types/models';

type Gate = 'admin' | 'accounts' | 'jobTools' | 'customers';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  group: 'work' | 'configuration' | 'account';
  gate?: Gate;
  isActive: (pathname: string) => boolean;
}

const startsWith = (prefix: string) => (p: string) => p === prefix || p.startsWith(`${prefix}/`);

// Role gating here is convenience only — the interface never renders a control the
// role cannot use; Firestore rules and Cloud Functions are the boundary.
const navItems: NavItem[] = [
  { to: '/', label: 'Home', icon: House, group: 'work', isActive: (p) => p === '/' },
  { to: '/jobs/new', label: 'New job', icon: CirclePlus, group: 'work', gate: 'jobTools', isActive: (p) => p === '/jobs/new' },
  {
    to: '/jobs', label: 'Live jobs', icon: CarFront, group: 'work',
    isActive: (p) => startsWith('/jobs')(p) && p !== '/jobs/new' && !p.startsWith('/jobs/archived'),
  },
  { to: '/archive', label: 'Archive', icon: Archive, group: 'work', isActive: (p) => p === '/archive' || p.startsWith('/jobs/archived') },
  { to: '/accounts', label: 'Accounts', icon: PoundSterling, group: 'work', gate: 'accounts', isActive: startsWith('/accounts') },
  { to: '/lookup', label: 'Reg check', icon: Search, group: 'work', gate: 'jobTools', isActive: startsWith('/lookup') },
  { to: '/customers', label: 'Customers', icon: Users, group: 'configuration', gate: 'customers', isActive: startsWith('/customers') },
  { to: '/subcontractors', label: 'Subcontractors', icon: Truck, group: 'configuration', gate: 'admin', isActive: startsWith('/subcontractors') },
  { to: '/users', label: 'Users and roles', icon: ShieldCheck, group: 'configuration', gate: 'admin', isActive: startsWith('/users') },
  { to: '/settings', label: 'Company settings', icon: SlidersHorizontal, group: 'configuration', gate: 'admin', isActive: startsWith('/settings') },
  { to: '/companies', label: 'Companies', icon: Building2, group: 'configuration', isActive: startsWith('/companies') },
  { to: '/change-password', label: 'Change password', icon: KeyRound, group: 'account', isActive: startsWith('/change-password') },
];

const groups: Array<{ id: NavItem['group']; label: string }> = [
  { id: 'work', label: 'Work' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'account', label: 'Account' },
];

function initialsOf(name?: string | null, email?: string | null): string {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function AppShell() {
  const { user, activeRole, isAdmin, activeCompanyName, signOutUser } = useAuth();
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [pathname]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const role = activeRole ?? undefined;
  const allowed: Record<Gate, boolean> = {
    admin: isAdmin,
    accounts: canPriceJobs(role) || canInvoiceJobs(role),
    jobTools: canCreateJobs(role),
    customers: canManageCustomers(role),
  };
  const visibleItems = navItems.filter((item) => !item.gate || allowed[item.gate]);

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-[18px] pb-3 pt-5">
        <span className="text-[19px] font-bold tracking-[-0.035em] text-ink">Checkover</span>
        <button
          onClick={() => setDrawerOpen(false)}
          className="flex h-9 w-9 items-center justify-center rounded-[10px] text-ink2 hover:bg-hover lg:hidden"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      <div className="px-3 pb-1.5">
        <CompanyPicker />
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 pb-4">
        {groups.map((group) => {
          const items = visibleItems.filter((i) => i.group === group.id);
          if (items.length === 0) return null;
          return (
            <div key={group.id}>
              <div className="eyebrow px-2.5 pb-1.5 pt-[18px]">{group.label}</div>
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  const active = item.isActive(pathname);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        aria-current={active ? 'page' : undefined}
                        className={`flex h-10 items-center gap-[11px] rounded-[10px] px-2.5 text-[14.5px] transition-colors duration-[180ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                          active ? 'bg-accenttint font-semibold text-accent' : 'font-medium text-ink2 hover:bg-accenttint hover:text-ink'
                        }`}
                      >
                        <Icon size={20} strokeWidth={1.8} aria-hidden />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="flex items-center gap-2.5 border-t border-hair px-3.5 py-3">
        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-sunken text-[12.5px] font-semibold text-ink" aria-hidden>
          {initialsOf(user?.displayName, user?.email)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-semibold text-ink">{user?.displayName || user?.email || 'Signed in'}</div>
          {activeRole && <div className="text-[12px] text-ink3">{ROLE_LABELS[activeRole]}</div>}
        </div>
        <button
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Use light appearance' : 'Use dark appearance'}
          title="Appearance"
          className="flex h-9 w-9 items-center justify-center rounded-[10px] text-ink2 hover:bg-hover hover:text-ink"
        >
          {theme === 'dark' ? <Sun size={20} strokeWidth={1.8} /> : <Moon size={20} strokeWidth={1.8} />}
        </button>
        <button
          onClick={() => void signOutUser()}
          aria-label="Sign out"
          title="Sign out"
          className="flex h-9 w-9 items-center justify-center rounded-[10px] text-st-cancelled hover:bg-hover"
        >
          <LogOut size={20} strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-shell flex bg-surface text-ink">
      <aside className="hidden w-64 shrink-0 border-r border-hair bg-sidebar lg:block">{sidebar}</aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/35 animate-fade-in" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] animate-drawer-in border-r border-hair bg-sidebar shadow-pop">
            {sidebar}
          </aside>
        </div>
      )}

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-2.5 border-b border-hair bg-elev px-3 lg:hidden">
          <button
            onClick={() => setDrawerOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-[10px] text-ink2 hover:bg-hover"
            aria-label="Open menu"
          >
            <Menu size={22} strokeWidth={1.8} />
          </button>
          <span className="text-[19px] font-bold tracking-[-0.035em] text-ink">Checkover</span>
          {activeCompanyName && (
            <span className="ml-auto flex min-w-0 items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink">
              <span className="h-2 w-2 shrink-0 rounded-full bg-st-completed" aria-hidden />
              <span className="truncate">{activeCompanyName}</span>
            </span>
          )}
        </header>
        <div className="flex-1 overflow-auto" id="main-scroll">
          <div className="mx-auto max-w-[1180px] px-4 pb-16 pt-6 sm:px-8 sm:pt-7">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
